/* One typed hook per M1 endpoint. No screen calls the client directly. */

import { useEffect, useState } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type Query,
} from "@tanstack/react-query";
import { ApiError, apiBlob, apiGet, apiSend, apiText, apiUpload } from "./client";
import type {
  DashboardSummary,
  DocumentDetail,
  DocumentEvent,
  DocumentFilters,
  DocumentSummary,
  Entity,
  EntityHealth,
  EntitySummary,
  Me,
  OutcomeLegs,
  Page,
  SettingCard,
  StageCounts,
  ThemeBranding,
  ThemeSettings,
  ThemeUpdateRequest,
  ValidationResult,
  WorkerAllocation,
} from "./types";

const FAST_POLL = 5000;
const SLOW_POLL = 30000;

/* `family` and `typeCode` are HUB-F08's list filters; /stage-counts ignores both, so
   useStageCounts never receives them and the strip stays family-agnostic. */
const FILTER_KEYS = [
  "direction", "stage", "state", "entityId", "q", "from", "to", "family", "typeCode", "limit",
] as const;

export function documentQuery(filters: DocumentFilters, cursor?: string | null): string {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => apiGet<Me>("/api/auth/me") });
}

export function useDashboardSummary() {
  return useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => apiGet<DashboardSummary>("/api/dashboard/summary"),
    refetchInterval: SLOW_POLL,
  });
}

/** `GET /api/asp/connection` (m4-rulings.md #59); no probe is run on request. */
export type AspConnection = {
  baseUrl: string;
  reachable: boolean;
  checkedAt: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureDetail: string | null;
  inFlightCount: number;
  trackingCount: number;
};

export function useAspConnection() {
  return useQuery({
    queryKey: ["asp", "connection"],
    queryFn: () => apiGet<AspConnection>("/api/asp/connection"),
    refetchInterval: SLOW_POLL,
  });
}

export function useEntities() {
  return useQuery({
    queryKey: ["entities"],
    queryFn: async () => (await apiGet<{ items: EntitySummary[] }>("/api/entities")).items,
  });
}

export function useEntity(entityId: string | undefined) {
  return useQuery({
    queryKey: ["entities", entityId],
    queryFn: () => apiGet<Entity>(`/api/entities/${entityId}`),
    enabled: Boolean(entityId),
  });
}

export function useEntityHealth(entityId: string | undefined) {
  return useQuery({
    queryKey: ["entities", entityId, "health"],
    queryFn: () => apiGet<EntityHealth>(`/api/entities/${entityId}/health`),
    enabled: Boolean(entityId),
    refetchInterval: SLOW_POLL,
  });
}

export function useDocuments(filters: DocumentFilters = {}, paused = false, enabled = true) {
  return useInfiniteQuery({
    queryKey: ["documents", "list", filters],
    queryFn: ({ pageParam }) =>
      apiGet<Page<DocumentSummary>>(`/api/documents${documentQuery(filters, pageParam)}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: paused || !enabled ? false : FAST_POLL,
    enabled,
  });
}

export function useStageCounts(
  filters: Pick<DocumentFilters, "direction" | "entityId"> = {},
  paused = false,
) {
  return useQuery({
    queryKey: ["documents", "stage-counts", filters],
    queryFn: () => apiGet<StageCounts>(`/api/documents/stage-counts${documentQuery(filters)}`),
    refetchInterval: paused ? false : FAST_POLL,
  });
}

export function useDocument(docId: string | undefined) {
  return useQuery({
    queryKey: ["documents", docId],
    queryFn: () => apiGet<DocumentDetail>(`/api/documents/${docId}`),
    enabled: Boolean(docId),
    // `state` is the backend's own summary; a document only moves while it is in flight.
    refetchInterval: (query: Query<DocumentDetail>) =>
      query.state.data?.state === "in_flight" ? FAST_POLL : false,
  });
}

export function useDocumentEvents(docId: string | undefined, enabled = true) {
  // Rides on the document query, which polls while in flight: each move (a new `updatedAt`)
  // reloads the timeline, and the previous list stays on screen until the new one lands.
  const version = useDocument(docId).data?.updatedAt;
  return useQuery({
    queryKey: ["documents", docId, "events", version],
    queryFn: async () =>
      (await apiGet<{ items: DocumentEvent[] }>(`/api/documents/${docId}/events`)).items,
    enabled: Boolean(docId) && enabled && version !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** m6-rulings.md #117: `/raw` and `/xml` carry the group admin's stated reason for the audit row. */
function accessReason(reason: string | undefined): RequestInit | undefined {
  return reason ? { headers: { "X-Access-Reason": reason } } : undefined;
}

export function useDocumentXml(docId: string | undefined, reason?: string) {
  return useQuery({
    queryKey: ["documents", docId, "xml", reason ?? null],
    queryFn: () => apiText(`/api/documents/${docId}/xml`, "application/xml", accessReason(reason)),
    enabled: Boolean(docId),
  });
}

export function useDocumentRaw(docId: string | undefined, reason?: string) {
  return useQuery({
    queryKey: ["documents", docId, "raw", reason ?? null],
    queryFn: () => apiText(`/api/documents/${docId}/raw`, "*/*", accessReason(reason)),
    enabled: Boolean(docId),
  });
}

export function useDocumentMapped(docId: string | undefined) {
  return useQuery({
    queryKey: ["documents", docId, "mapped"],
    queryFn: () => apiGet<Record<string, unknown>>(`/api/documents/${docId}/mapped`),
    enabled: Boolean(docId),
  });
}

export function useDocumentValidation(docId: string | undefined) {
  return useQuery({
    queryKey: ["documents", docId, "validation"],
    queryFn: () => apiGet<ValidationResult>(`/api/documents/${docId}/validation`),
    enabled: Boolean(docId),
  });
}

/** The sealed record (m4-rulings.md #59). 404 until the document reaches `archived`. */
export type DocumentArchive = { xmlSha256: string; sealedAt: string };

export function useDocumentArchive(docId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["documents", docId, "archive"],
    queryFn: () => apiGet<DocumentArchive>(`/api/documents/${docId}/archive`),
    // Asking before the document is sealed would answer 404 on a perfectly normal page.
    enabled: Boolean(docId) && enabled,
    retry: false,
  });
}

export function useDocumentLegs(docId: string | undefined) {
  return useQuery({
    queryKey: ["documents", docId, "legs"],
    queryFn: () => apiGet<OutcomeLegs>(`/api/documents/${docId}/legs`),
    enabled: Boolean(docId),
    refetchInterval: (query: Query<OutcomeLegs>) => {
      const legs = query.state.data;
      if (!legs) return FAST_POLL;
      const pending = [legs.aspAck, legs.peppolMls, legs.otaRef].some(
        (leg) => !leg || leg.status === "pending",
      );
      return pending ? FAST_POLL : false;
    },
  });
}

export type QrState = { url: string | null; isLoading: boolean; error: Error | null };

/** Object URL rather than a query: the blob has to be revoked on unmount. */
export function useDocumentQr(docId: string | undefined): QrState {
  const [state, setState] = useState<QrState>({ url: null, isLoading: true, error: null });

  useEffect(() => {
    if (!docId) {
      setState({ url: null, isLoading: false, error: null });
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ url: null, isLoading: true, error: null });

    apiBlob(`/api/documents/${docId}/qr.png`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, isLoading: false, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const missing = error instanceof ApiError && error.status === 404;
        setState({ url: null, isLoading: false, error: missing ? null : (error as Error) });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId]);

  return state;
}

/** `enabled: false` for the portal surface: the theme is hub-wide and the portal is out of
    scope for it, so a portal session must not make a request the hub console owns. */
export function useThemeSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["theme"],
    queryFn: () => apiGet<ThemeSettings>("/api/theme"),
    enabled: options.enabled ?? true,
  });
}

/** Public brand read for the sign-in page (no session yet): name, tagline, logo, favicon. */
export function useBranding(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["branding"],
    queryFn: () => apiGet<ThemeBranding>("/api/theme/branding"),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useUpdateThemeSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ThemeUpdateRequest) => apiSend<ThemeSettings>("PATCH", "/api/theme", body),
    onSuccess: (row) => {
      queryClient.setQueryData(["theme"], row);
      void queryClient.invalidateQueries({ queryKey: ["branding"] });
    },
  });
}

export function useUploadThemeAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file }: { kind: "logo" | "favicon"; file: File }) =>
      apiUpload<ThemeSettings>(`/api/theme/assets/${kind}`, file),
    onSuccess: (row) => {
      queryClient.setQueryData(["theme"], row);
      void queryClient.invalidateQueries({ queryKey: ["branding"] });
    },
  });
}

export function useReprocess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      apiSend<DocumentSummary>("POST", `/api/documents/${docId}/reprocess`),
    onSuccess: (_row, docId) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["documents", docId] });
    },
  });
}

/** HUB-I08: stop/resume one company's claims. Never a change to the deployed replica
 * count — that stays a deployment action (HUB_PIPELINE_REPLICAS, I04/I09). */
export function usePauseEntity(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      apiSend<Entity>("POST", `/api/entities/${entityId}/pause`, { reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["entities", entityId] }),
  });
}

export function useResumeEntity(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiSend<Entity>("POST", `/api/entities/${entityId}/resume`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["entities", entityId] }),
  });
}

/** HUB-I08: force the ASP lane open, or resume it (through half_open, never straight to
 * closed) — an operator override, independent of the ordinary failure count. */
export function useAspBreakerControl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: "pause" | "resume") => apiSend<{ status: string }>("POST", `/api/asp/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["dashboard", "summary"] }),
  });
}

/** HUB-I08: the console's live-editable operational knobs. */
export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => apiGet<{ items: SettingCard[] }>("/api/settings"),
  });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: number }) =>
      apiSend<SettingCard>("POST", `/api/settings/${key}`, { value }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
  });
}

/** HUB-I09: every company with a per-company cap on claim_due's share of the pool,
 * beside the group's actual deployed replica count. */
export function useWorkerAllocation() {
  return useQuery({
    queryKey: ["workerAllocation"],
    queryFn: () => apiGet<WorkerAllocation>("/api/entities/worker-ceilings"),
  });
}

export function useSetWorkerCeiling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, ceiling }: { entityId: string; ceiling: number | null }) =>
      apiSend<Entity>("PUT", `/api/entities/${entityId}/worker-ceiling`, { ceiling }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workerAllocation"] }),
  });
}
